import { useEffect } from 'react';

interface SchemaOrgProps {
  type: 'LocalBusiness' | 'Service' | 'Product' | 'Offer' | 'FAQPage' | 'Article' | 'WebSite' | 'HowTo';
  data: any;
}

const SchemaOrg: React.FC<SchemaOrgProps> = ({ type, data }) => {
  const generateSchema = () => {
    switch (type) {
      case 'LocalBusiness':
        return {
          '@context': 'https://schema.org',
          '@type': 'LocalBusiness',
          '@id': data.url || 'https://readdy.ai/',
          name: data.name || '專業搬家公司',
          image: data.image || 'https://public.readdy.ai/ai/img_res/b1578ed5dc60c0beac00b20889b79f22.jpg',
          url: data.url || 'https://readdy.ai/',
          telephone: data.telephone || '+886-2-1234-5678',
          priceRange: data.priceRange || '$$',
          address: {
            '@type': 'PostalAddress',
            streetAddress: data.address?.streetAddress || '新北市板橋區文化路一段188號',
            addressLocality: data.address?.addressLocality || '新北市',
            addressRegion: data.address?.addressRegion || '新北市',
            postalCode: data.address?.postalCode || '220',
            addressCountry: 'TW'
          },
          geo: {
            '@type': 'GeoCoordinates',
            latitude: data.geo?.latitude || 25.0142,
            longitude: data.geo?.longitude || 121.4627
          },
          openingHoursSpecification: data.openingHours || [
            {
              '@type': 'OpeningHoursSpecification',
              dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
              opens: '00:00',
              closes: '23:59'
            }
          ],
          sameAs: data.sameAs || [
            'https://www.facebook.com/movingcompany',
            'https://www.instagram.com/movingcompany'
          ],
          areaServed: data.areaServed || [
            {
              '@type': 'City',
              name: '台北市',
              geo: {
                '@type': 'GeoCoordinates',
                latitude: 25.033,
                longitude: 121.5654
              }
            },
            {
              '@type': 'City',
              name: '新北市',
              geo: {
                '@type': 'GeoCoordinates',
                latitude: 25.0142,
                longitude: 121.4627
              }
            },
            {
              '@type': 'City',
              name: '桃園市',
              geo: {
                '@type': 'GeoCoordinates',
                latitude: 24.9936,
                longitude: 121.301
              }
            }
          ],
          hasOfferCatalog: data.hasOfferCatalog || {
            '@type': 'OfferCatalog',
            name: '搬家服務項目',
            itemListElement: [
              {
                '@type': 'Offer',
                itemOffered: {
                  '@type': 'Service',
                  name: '家庭搬家',
                  description: '提供完整的家庭搬遷服務'
                }
              },
              {
                '@type': 'Offer',
                itemOffered: {
                  '@type': 'Service',
                  name: '辦公室搬遷',
                  description: '專業的辦公室搬遷服務'
                }
              },
              {
                '@type': 'Offer',
                itemOffered: {
                  '@type': 'Service',
                  name: '鋼琴搬運',
                  description: '專業鋼琴搬運服務'
                }
              }
            ]
          }
        };

      case 'Service':
        return {
          '@context': 'https://schema.org',
          '@type': 'Service',
          name: data.name,
          description: data.description,
          image: data.image || 'https://readdy.ai/api/search-image?query=professional%20moving%20service%20team%20working%20efficiently%20with%20modern%20equipment%20clean%20organized%20background%20high%20quality%20commercial%20photography&width=1200&height=630&seq=schema-service&orientation=landscape',
          provider: {
            '@type': 'LocalBusiness',
            name: data.provider?.name || '專業搬家公司',
            telephone: data.provider?.telephone || '+886-2-1234-5678',
            address: data.provider?.address || {
              '@type': 'PostalAddress',
              streetAddress: data.provider?.address?.streetAddress || '新北市板橋區文化路一段188號',
              addressLocality: data.provider?.address?.addressLocality || '新北市',
              addressRegion: data.provider?.address?.addressRegion || '新北市',
              postalCode: data.provider?.address?.postalCode || '220',
              addressCountry: 'TW'
            }
          },
          areaServed: data.areaServed || '台灣',
          offers: {
            '@type': 'Offer',
            price: data.price,
            priceCurrency: 'TWD',
            availability: 'https://schema.org/InStock'
          }
        };

      case 'Product':
        return {
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: data.name,
          description: data.description,
          image: data.image || 'https://readdy.ai/api/search-image?query=professional%20moving%20service%20product%20high%20quality%20commercial%20photography%20modern%20clean%20background%20organized%20equipment&width=800&height=600&seq=schema-product&orientation=landscape',
          brand: {
            '@type': 'Brand',
            name: data.brand || '專業搬家公司'
          },
          offers: {
            '@type': 'Offer',
            price: data.price,
            priceCurrency: 'TWD',
            availability: 'https://schema.org/InStock',
            priceValidUntil: data.priceValidUntil,
            seller: data.seller || {
              '@type': 'Organization',
              name: '專業搬家公司',
              address: {
                '@type': 'PostalAddress',
                streetAddress: data.seller?.address?.streetAddress || '新北市板橋區文化路一段188號',
                addressLocality: data.seller?.address?.addressLocality || '新北市',
                addressRegion: data.seller?.address?.addressRegion || '新北市',
                postalCode: data.seller?.address?.postalCode || '220',
                addressCountry: 'TW'
              }
            },
            shippingDetails: data.shippingDetails || {
              '@type': 'OfferShippingDetails',
              shippingRate: {
                '@type': 'MonetaryAmount',
                value: '0',
                currency: 'TWD'
              },
              shippingDestination: {
                '@type': 'DefinedRegion',
                addressCountry: 'TW'
              },
              deliveryTime: {
                '@type': 'ShippingDeliveryTime',
                handlingTime: {
                  '@type': 'QuantitativeValue',
                  minValue: 0,
                  maxValue: 1,
                  unitCode: 'DAY'
                },
                transitTime: {
                  '@type': 'QuantitativeValue',
                  minValue: 0,
                  maxValue: 0,
                  unitCode: 'DAY'
                }
              }
            },
            hasMerchantReturnPolicy: data.hasMerchantReturnPolicy || {
              '@type': 'MerchantReturnPolicy',
              applicableCountry: 'TW',
              returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
              merchantReturnDays: 7,
              returnMethod: 'https://schema.org/ReturnByMail',
              returnFees: 'https://schema.org/FreeReturn'
            }
          }
        };

      case 'Offer':
        return {
          '@context': 'https://schema.org',
          '@type': 'Offer',
          name: data.name,
          description: data.description,
          image: data.image || 'https://readdy.ai/api/search-image?query=special%20moving%20service%20offer%20promotion%20high%20quality%20commercial%20photography%20modern%20clean%20background%20attractive%20deal&width=800&height=600&seq=schema-offer&orientation=landscape',
          price: data.price,
          priceCurrency: 'TWD',
          availability: 'https://schema.org/InStock',
          validFrom: data.validFrom,
          validThrough: data.validThrough,
          seller: data.seller || {
            '@type': 'Organization',
            name: '專業搬家公司',
            address: {
              '@type': 'PostalAddress',
              streetAddress: data.seller?.address?.streetAddress || '新北市板橋區文化路一段188號',
              addressLocality: data.seller?.address?.addressLocality || '新北市',
              addressRegion: data.seller?.address?.addressRegion || '新北市',
              postalCode: data.seller?.address?.postalCode || '220',
              addressCountry: 'TW'
            }
          },
          shippingDetails: data.shippingDetails || {
            '@type': 'OfferShippingDetails',
            shippingRate: {
              '@type': 'MonetaryAmount',
              value: '0',
              currency: 'TWD'
            },
            shippingDestination: {
              '@type': 'DefinedRegion',
              addressCountry: 'TW'
            },
            deliveryTime: {
              '@type': 'ShippingDeliveryTime',
              handlingTime: {
                '@type': 'QuantitativeValue',
                minValue: 0,
                maxValue: 1,
                unitCode: 'DAY'
              },
              transitTime: {
                '@type': 'QuantitativeValue',
                minValue: 0,
                maxValue: 0,
                unitCode: 'DAY'
              }
            }
          },
          hasMerchantReturnPolicy: data.hasMerchantReturnPolicy || {
            '@type': 'MerchantReturnPolicy',
            applicableCountry: 'TW',
            returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
            merchantReturnDays: 7,
            returnMethod: 'https://schema.org/ReturnByMail',
            returnFees: 'https://schema.org/FreeReturn'
          }
        };

      case 'FAQPage':
        return {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: data.questions?.map((q: any) => ({
            '@type': 'Question',
            name: q.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: q.answer
            }
          })) || []
        };

      case 'Article':
        return {
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: data.headline,
          description: data.description,
          image: data.image,
          author: {
            '@type': 'Person',
            name: data.author || '專業搬家顧問'
          },
          publisher: {
            '@type': 'Organization',
            name: '專業搬家公司',
            logo: {
              '@type': 'ImageObject',
              url: 'https://readdy.ai/api/search-image?query=professional%20moving%20company%20logo%20modern%20clean%20simple%20design%20high%20quality&width=200&height=60&seq=logo-schema&orientation=landscape'
            }
          },
          datePublished: data.datePublished,
          dateModified: data.dateModified || data.datePublished,
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': data.url
          }
        };

      case 'WebSite':
        return {
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: data.name || '專業搬家公司',
          url: data.url || 'https://readdy.ai/',
          potentialAction: {
            '@type': 'SearchAction',
            target: {
              '@type': 'EntryPoint',
              urlTemplate: `${data.url || 'https://readdy.ai/'}/search?q={search_term_string}`
            },
            'query-input': 'required name=search_term_string'
          }
        };

      case 'HowTo':
        return {
          '@context': 'https://schema.org',
          '@type': 'HowTo',
          name: data.name,
          description: data.description,
          image: data.image,
          totalTime: data.totalTime,
          estimatedCost: data.estimatedCost && {
            '@type': 'MonetaryAmount',
            currency: 'TWD',
            value: data.estimatedCost
          },
          tool: data.tool?.map((t: string) => ({
            '@type': 'HowToTool',
            name: t
          })) || [],
          supply: data.supply?.map((s: string) => ({
            '@type': 'HowToSupply',
            name: s
          })) || [],
          step: data.steps?.map((step: any, index: number) => ({
            '@type': 'HowToStep',
            position: index + 1,
            name: step.name,
            text: step.text,
            image: step.image,
            url: step.url
          })) || []
        };

      default:
        return {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: data?.name || '專業搬家公司',
          url: data?.url || 'https://readdy.ai/'
        };
    }
  };

  const schema = generateSchema();

  if (!schema) {
    return null;
  }

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

export default SchemaOrg;
